"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import { StarRating } from "./star-rating";
import { Button } from "@/ui/components/ui/button";
import { defaultLocale, formatDate, isValidLocale, type Locale } from "@/config/locale";

interface ReviewData {
	id: string;
	rating: number;
	name: string;
	comment: string;
	verified: boolean;
	createdAt: Date | string;
	/** Server-formatted date; reviews fetched in the browser leave it unset. */
	createdAtLabel?: string;
}

interface ProductReviewsProps {
	productSlug: string;
	initialReviews?: ReviewData[];
}

export function ProductReviews({ productSlug, initialReviews }: ProductReviewsProps) {
	const t = useTranslations("reviews");
	const { locale: localeParam } = useParams<{ locale: string }>();
	const locale: Locale = useMemo(
		() => (localeParam && isValidLocale(localeParam) ? localeParam : defaultLocale),
		[localeParam],
	);
	const [reviews, setReviews] = useState<ReviewData[]>(initialReviews ?? []);
	const [showForm, setShowForm] = useState(false);
	const [formData, setFormData] = useState({ name: "", comment: "", rating: 0 });
	const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

	const fetchReviews = useCallback(async () => {
		try {
			const res = await fetch(`/api/reviews?productSlug=${encodeURIComponent(productSlug)}`);
			if (res.ok) {
				const data = (await res.json()) as { reviews: ReviewData[] };
				setReviews(data.reviews);
			}
		} catch {
			// Silently fail - reviews are not critical
		}
	}, [productSlug]);

	useEffect(() => {
		if (!initialReviews) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			fetchReviews();
		}
	}, [initialReviews, fetchReviews]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (formData.rating === 0 || !formData.name.trim() || !formData.comment.trim()) return;

		setStatus("loading");
		try {
			const res = await fetch("/api/reviews", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ productSlug, ...formData }),
			});

			if (res.ok) {
				setFormData({ name: "", comment: "", rating: 0 });
				setShowForm(false);
				setStatus("success");
				setTimeout(() => setStatus("idle"), 3000);
			} else {
				setStatus("error");
			}
		} catch {
			setStatus("error");
		}
	};

	const avgRating = reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0;

	return (
		<section aria-labelledby="reviews-heading" className="space-y-6">
			{/* Header */}
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="space-y-1">
					<h2 id="reviews-heading" className="text-lg font-semibold text-foreground">
						{t("title")}
					</h2>
					{reviews.length > 0 && (
						<div className="flex items-center gap-2">
							<StarRating rating={Math.round(avgRating)} size="sm" />
							<span className="text-sm font-medium tabular-nums text-foreground">{avgRating.toFixed(1)}</span>
							<span className="text-sm text-muted-foreground">
								&middot; {t("basedOn", { count: reviews.length })}
							</span>
						</div>
					)}
				</div>
				<Button variant="outline-solid" size="sm" onClick={() => setShowForm(!showForm)}>
					{t("writeReview")}
				</Button>
			</div>

			{/* Success message */}
			{status === "success" && (
				<div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">
					<svg className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
						<path
							fillRule="evenodd"
							d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
							clipRule="evenodd"
						/>
					</svg>
					{t("pendingModeration")}
				</div>
			)}

			{/* Review Form */}
			{showForm && (
				<form
					onSubmit={handleSubmit}
					className="bg-secondary/30 space-y-4 rounded-xl border border-border p-4 sm:p-5"
				>
					<div>
						<label className="mb-1.5 block text-sm font-medium text-foreground">{t("rating")}</label>
						<StarRating
							rating={formData.rating}
							interactive
							onRate={(r) => setFormData((prev) => ({ ...prev, rating: r }))}
						/>
					</div>
					<div className="grid gap-4 sm:grid-cols-2">
						<div>
							<label className="mb-1.5 block text-sm font-medium text-foreground">{t("name")}</label>
							<input
								type="text"
								value={formData.name}
								onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
								placeholder={t("namePlaceholder")}
								className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
								required
							/>
						</div>
						<div className="sm:col-span-2">
							<label className="mb-1.5 block text-sm font-medium text-foreground">{t("comment")}</label>
							<textarea
								value={formData.comment}
								onChange={(e) => setFormData((prev) => ({ ...prev, comment: e.target.value }))}
								placeholder={t("commentPlaceholder")}
								rows={3}
								className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
								required
							/>
						</div>
					</div>
					<div className="flex items-center gap-3">
						<Button type="submit" size="sm" disabled={status === "loading" || formData.rating === 0}>
							{status === "loading" ? t("submitting") : t("submit")}
						</Button>
						<Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)}>
							{t("cancel") ?? "Cancel"}
						</Button>
					</div>
					{status === "error" && <p className="text-sm text-destructive">{t("error")}</p>}
				</form>
			)}

			{/* Review List */}
			{reviews.length > 0 ? (
				<div className="divide-y divide-border">
					{reviews.map((review) => (
						<div key={review.id} className="py-5 first:pt-0 last:pb-0">
							<div className="flex items-start gap-3">
								{/* Avatar */}
								<div className="bg-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-primary">
									{review.name.charAt(0).toUpperCase()}
								</div>
								<div className="min-w-0 flex-1">
									<div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
										<span className="text-sm font-semibold text-foreground">{review.name}</span>
										{review.verified && (
											<span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400">
												<svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
													<path
														fillRule="evenodd"
														d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
														clipRule="evenodd"
													/>
												</svg>
												{t("verified")}
											</span>
										)}
										<span className="text-xs text-muted-foreground">
											{review.createdAtLabel ??
												formatDate(new Date(review.createdAt), { timeZone: "UTC" }, locale)}
										</span>
									</div>
									<div className="mt-1">
										<StarRating rating={review.rating} size="sm" />
									</div>
									<p className="mt-2 text-sm leading-relaxed text-muted-foreground">{review.comment}</p>
								</div>
							</div>
						</div>
					))}
				</div>
			) : !showForm ? (
				<p className="py-6 text-center text-sm text-muted-foreground">{t("noReviews")}</p>
			) : null}
		</section>
	);
}
